import React, { useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { quoteFormSelector, verticalLinkContentSelector, verticalCopyContentSelector } from 'ctm-selectors';
import { Text, Checkbox, withRules } from 'ctm-components';
import { connect, useSelector } from 'react-redux';
import { useBrandContentFactory } from 'ctm-app/client/hooks/useBrandContentFactory';
import { CallCentreHoursModal } from './call_centre_hours_dialog';
import { TrustedPartnersModal } from './trusted_partners_dialog';

const _OptinPrivacy = ({ dispatch, stateProps, value }) => {
    const modalEventListeners = useRef([]);
    const companyName = useSelector((state) => verticalCopyContentSelector(state).get('application_details_opt_in_checkbox_brand_name'));
    const privacyPolicyUrl = useSelector((state) => verticalLinkContentSelector(state).get('application_details_opt_in_checkbox_privacy_policy_url'));
    const fsgUrl = useSelector((state) => verticalLinkContentSelector(state).get('application_details_opt_in_checkbox_financial_services_guide_url'));
    const termsOfUseUrl = useSelector((state) => verticalLinkContentSelector(state).get('application_details_opt_in_checkbox_terms_of_use_url'));
    const brandContentFactory = useBrandContentFactory();

    const handleOpeningHoursDialogClose = () => {
        dispatch({
            type: 'SET_RESULTS_CONFIG_VALUE',
            key: 'helpers.callCentreHours',
            value: { open: false }
        });
    };

    const showOpeningHoursDialog = () => {
        dispatch({
            type: 'SET_RESULTS_CONFIG_VALUE',
            key: 'helpers.callCentreHours',
            value: { open: true }
        });
    };

    const handleTrustedPartnersDialogClose = () => {
        dispatch({
            type: 'SET_RESULTS_CONFIG_VALUE',
            key: 'helpers.trustedPartners',
            value: { open: false }
        });
    };

    const showTrustedPartnersDialog = () => {
        dispatch({
            type: 'SET_RESULTS_CONFIG_VALUE',
            key: 'helpers.trustedPartners',
            value: { open: true }
        });
    };

    // Add callback to include event listeners for the modals off the elements found in the contactDetailOptInPrivacyMessage
    // currently only trustedPartnersOpen and callCentreHoursOpen is supported.
    const optInContentElementHandler = useCallback((node) => {
        const trustedPartnerHandler = (event) => {
            event.preventDefault();
            event.stopImmediatePropagation();
            showTrustedPartnersDialog();
        };
        const callCenterHandler = (event) => {
            event.preventDefault();
            event.stopImmediatePropagation();
            showOpeningHoursDialog();
        };

        // when component gets updated, it calls this ref function again.
        // node may become null on clean-up or certain situations.
        if (node !== null) {
            node.querySelector('[data-id=trustedPartnersOpen]').addEventListener('click', trustedPartnerHandler);
            node.querySelector('[data-id=callCentreHoursOpen]').addEventListener('click', callCenterHandler);
            modalEventListeners.current.push(() => { node.querySelector('[data-id=trustedPartnersOpen]').removeEventListener('click', trustedPartnerHandler); });
            modalEventListeners.current.push(() => { node.querySelector('[data-id=callCentreHoursOpen]').removeEventListener('click', callCenterHandler); });
        }

        // We are currently using React 18 which uses null to indicate the reference has been detached/unmounted
        // React 19 would move to a similar design as useEffect. Albeit React 18 introduced it as a canary feature but can't get it to work.
        // See https://18.react.dev/reference/react-dom/components/common#ref-callback-parameters
        // and https://react.dev/blog/2024/12/05/react-19#cleanup-functions-for-refs.
        if (node == null && modalEventListeners.current.length > 0) {
            modalEventListeners.current.forEach((unsubscribe) => unsubscribe());
            modalEventListeners.current = [];
        }
    },
    // Using companyName as that's the seem to trigger once all the brand switching has settled.
    // this ensures that the callback gets updated only after the new opt-in DOM element is rendered
    [companyName]);

    const renderLabel = () => {
        const optInText = brandContentFactory.contactDetailOptInPrivacyMessage({ termsOfUseUrl, companyName, fsgUrl, privacyPolicyUrl });
        return (
            <Text
                ref={optInContentElementHandler}
                variant="underline_anchors"
                size="small"
                innerHTML={optInText} />
        );
    };

    const checkedActions = [
        {
            type: 'SET_FORM_VALUE',
            identifier: 'contactDetails.optinPrivacy',
            value: 'Y',
            stateKey: 'vertical.quote'
        }
    ];

    const uncheckedActions = [
        {
            type: 'SET_FORM_VALUE',
            identifier: 'contactDetails.optinPrivacy',
            value: 'N',
            stateKey: 'vertical.quote'
        }
    ];

    const schema = {
        stateKey: 'vertical.quote',
        field: {
            type: 'checkbox',
            UIComponent: 'checkbox_medium_rounded',
            labelEl: renderLabel(),
            variant: 'small_text',
            default: '',
            value: 'Y'
        },
        identifier: 'contactDetails.optinPrivacy',
        eventProps: {
            onChange: {
                checked: {
                    actions: checkedActions
                },
                unchecked: {
                    actions: uncheckedActions
                }
            }
        }
    };

    return (
        <>
            <TrustedPartnersModal closeModal={handleTrustedPartnersDialogClose} />
            <CallCentreHoursModal closeModal={handleOpeningHoursDialogClose} centres={['Car', 'Energy', 'Health']} />
            <Checkbox schema={schema} value={value} dispatch={dispatch} key={schema.identifier} />
        </>
    );
};

_OptinPrivacy.propTypes = {
    dispatch: PropTypes.func.isRequired,
    stateProps: PropTypes.shape({
        isOpen: PropTypes.bool
    }),
    value: PropTypes.string
};

_OptinPrivacy.defaultProps = {
    value: 'N'
};

/**
 * mapStateToProps - sets up the rules required by this component
 * @param {Object} state - Redux store state.
 * @return {Object} - The state attributes to add to props.
 */
const mapStateToProps = (state) => {
    const form = quoteFormSelector(state);
    const value = form.getIn(['contactDetails', 'optinPrivacy']);

    return {
        value
    };
};

const OptinPrivacy = withRules(connect(mapStateToProps)(_OptinPrivacy));

export { _OptinPrivacy, OptinPrivacy };
